/**
 * Helpers shared by the Contentful fetchers under `src/contentful/`: the slug
 * list that feeds `getStaticPaths`, and two builders that dress fixture data up
 * as an Axios or Contentful response.
 *
 * Position:
 * - `getAllContentfulPaths` runs at build time, in `getStaticPaths` or
 *   `getStaticProps`. Callers: `src/pages/about-us/family-of-companies/[slug].tsx`
 *   (`foCCompany`), `src/pages/metal-resources/metal-market-intelligence/podcast/[slug].tsx`
 *   (`podcast`) and `src/components/Blog/BlogCategoryMetaHead.tsx` → `isCategoryPage`
 *   (`blogCategory`; `isCategoryPage` itself has no caller).
 * - `wrapDataWithAxiosResponse` has one caller,
 *   `src/contentful/homepage-below-search-alert/helpers/getMockData.functions.ts`.
 *   `wrapDataInContentfulResponse` has no importer.
 * - Requests go through `@ryerson/frontend.contentful` → `fetchContentfulData`,
 *   which makes up to three attempts, six seconds apart, before it throws.
 *
 * Rules a change must keep:
 * - A failed Contentful call during `next build` fails the build (web-architecture
 *   ADR-0033 §1): log with `displayAxiosError`, then rethrow. Never return `[]`.
 * - `getAllContentfulPaths` sends one request with no `limit` or `skip`, so it gets
 *   Contentful's default first page of 100 entries and doesn't compare against
 *   `total`. ADR-0033 §2 says a short result must fail the build; this helper does
 *   not check for one.
 */
import {
	ContentfulAPIResponse,
	displayAxiosError,
	fetchContentfulData,
} from "@ryerson/frontend.contentful";
import { AxiosError, AxiosResponse } from "axios";

/**
 * Wraps `data` in an object shaped like a 200 `AxiosResponse` from the Contentful
 * Delivery API, for unit tests and mock data. The headers and config are fixed
 * sample values, not taken from a real request.
 */
export const wrapDataWithAxiosResponse = <T,>(data: T) => {
	return {
		data: data,
		status: 200,
		statusText: "",
		headers: {
			"content-length": "14590",
			"content-type": "application/vnd.contentful.delivery.v1+json",
			etag: 'W/"2785585331442361778"',
		},
		config: {
			method: "get",
			headers: {
				Accept: "application/json, text/plain, */*",
			},
			transformRequest: [null],
			transformResponse: [null],
			timeout: 0,
			xsrfCookieName: "XSRF-TOKEN",
			xsrfHeaderName: "X-XSRF-TOKEN",
			maxContentLength: -1,
			maxBodyLength: -1,
			transitional: {
				silentJSONParsing: true,
				forcedJSONParsing: true,
				clarifyTimeoutError: false,
			},
		},
		request: {},
	} as unknown as AxiosResponse<T>;
};

/**
 * Wraps one entry as a single-item Contentful collection (`items: [data]`,
 * `total: 1`), for unit tests. The result is cast back to `T`, so the static
 * type doesn't describe the collection shape.
 */
export const wrapDataInContentfulResponse = <T,>(data: T) => {
	return {
		includes: {},
		items: [data],
		total: 1,
		skip: 0,
		limit: 100,
	} as unknown as T;
};

/**
 * Returns the `fields.slug` of every entry of `contentType`, for `getStaticPaths`.
 *
 * Reads the `en-US` locale only: one slug per entry, shared by every language.
 * Entries with no slug, or an empty one, are dropped. Only the first 100 entries
 * Contentful returns are read (no paging).
 *
 * @throws The Contentful or network error, after logging it, so the build fails.
 */
export const getAllContentfulPaths = async function (contentType: string): Promise<string[]> {
	try {
		const response = await fetchContentfulData<ContentfulAPIResponse<any>>(contentType, [
			{ name: "locale", value: "en-US" },
			{ name: "select", value: "fields.slug" },
		]);

		const paths: string[] = response.data.items
			.filter((item) => item.fields?.slug && item.fields.slug.length > 0)
			.map((filteredItem) => filteredItem.fields.slug);

		return paths;
	} catch (error: any) {
		displayAxiosError(error as AxiosError);
		throw error;
	}
};
